import React, { useState, useEffect } from 'react';
import { enhancedApi } from '../services/api';
import { formatDisplayDate, getTimezoneInfo } from '../utils/timezone';
import { 
  Calendar,
  Download,
  Users,
  Building2,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Upload,
  FileText,
  BarChart3,
  Database
} from 'lucide-react';

interface EMRPatient {
  NOTE_NO: string;
  NOTE_DATE: string;
  PATIENTID: string;
  VISITID: string;
  TRF_ID: string;
  AGE: string;
  TIME_OF_DEATH: string;
  PLACE_OF_DEATH: string;
  PLACE_DESCRIPTION: string;
  CAUSE_OF_DEATH: string;
  CREATED_TIME: string;
  CAUSEDEATH_DIRECT: string;
  CAUSEDEATH_UNDERLYING1: string;
  CAUSEDEATH_UNDERLYING2: string;
  CAUSEDEATH_UNDERLYING3: string;
  INTERVAL_DIRECT: string;
  INTERVAL_UNDERLYING1: string;
  INTERVAL_UNDERLYING2: string;
  INTERVAL_UNDERLYING3: string;
  INTERVAL_OTHER: string;
  MANNER_OF_DEATH: string;
  INJURY_TYPE: string;
  DEPT: string;
  UNITID: number;
  VISIT_TIME: string;
  TRF_DATE: string;
  DISCHARGE_DATE: string;
  PROV_DIAGNOSIS: string;
  FINAL_DIAGNOSIS: string;
  unit_name: string;
  unit_id: number | null;
}

interface EMRStatistics {
  total_patients: number;
  unit_breakdown: { [key: string]: number };
}

interface EMRResponse {
  success: boolean;
  data: {
    patients: EMRPatient[];
    statistics: EMRStatistics;
    month: number;
    year: number;
  };
  message?: string;
}

export const EnhancedEMRImport: React.FC = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [emrData, setEmrData] = useState<EMRPatient[]>([]);
  const [statistics, setStatistics] = useState<EMRStatistics | null>(null);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'oracle' | 'mysql' | null>(null);
  const [lastSyncDate, setLastSyncDate] = useState<string>('');
  const [newlyImportedCount, setNewlyImportedCount] = useState<number>(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

  const fetchEMRData = async () => {
    try {
      setLoading(true);
      setError(null);
      setEmrData([]);
      setStatistics(null);
      setSelectedPatients([]);
      setImportResult(null);
      setDataSource(null);
      setNewlyImportedCount(0);

      const response = await enhancedApi.admin.getEnhancedEMRData(selectedMonth, selectedYear);
      
      if (response.success && 'data' in response) {
        setEmrData(response.data.patients);
        setStatistics(response.data.statistics);
        setDataSource(response.data.data_source);
        setLastSyncDate(response.data.last_sync_date || '');
        
        // Auto-select all patients for import if data source is Oracle
        if (response.data.data_source === 'oracle') {
          const allPatientIds = response.data.patients.map((p: EMRPatient) => p.PATIENTID);
          setSelectedPatients(allPatientIds);
        }
      } else {
        setError(response.message || 'Failed to fetch EMR data');
      }
    } catch (error: any) {
      console.error('Failed to fetch EMR data:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch EMR data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedPatients.length === emrData.length) {
      setSelectedPatients([]);
    } else {
      setSelectedPatients(emrData.map(patient => patient.PATIENTID));
    }
  };

  const handleSelectPatient = (patientId: string) => {
    setSelectedPatients(prev => 
      prev.includes(patientId) 
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  const handleImportSelected = async () => {
    if (selectedPatients.length === 0) {
      setError('Please select at least one patient to import');
      return;
    }

    try {
      setImporting(true);
      setError(null);

      const selectedPatientData = emrData.filter(patient => 
        selectedPatients.includes(patient.PATIENTID)
      );

      const response = await enhancedApi.admin.importSelectedPatients(selectedPatientData);
      
      if (response.success) {
        setImportResult(response.data);
        setNewlyImportedCount(response.data.imported_count || 0);
        setSelectedPatients([]);
        // Refresh the data to show updated status
        await fetchEMRData();
      } else {
        setError(response.message || 'Failed to import patients');
      }
    } catch (error: any) {
      console.error('Failed to import patients:', error);
      setError(error.response?.data?.message || error.message || 'Failed to import patients');
    } finally {
      setImporting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Month/Year Selection */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex items-center space-x-4 mb-4">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Select Month and Year</h2>
        </div>
        
        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          
          <button
            onClick={fetchEMRData}
            disabled={loading}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            <span>{loading ? 'Fetching...' : 'Fetch EMR Data'}</span>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Import Result */}
      {importResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <div>
              <p className="text-green-800 font-medium">Import Completed Successfully!</p>
              <p className="text-green-700 text-sm">
                Imported: {importResult.imported} patients | 
                Skipped: {importResult.skipped} duplicates
              </p>
              {importResult.errors.length > 0 && (
                <p className="text-red-700 text-sm">
                  Errors: {importResult.errors.length}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Data Source Indicator */}
      {dataSource && (
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Data Source</p>
              <p className="text-lg font-bold text-gray-900">
                {dataSource === 'oracle' ? 'Oracle EMR Database' : 'Local MySQL Database'}
              </p>
              {lastSyncDate && (
                <p className="text-xs text-gray-500 mt-1">
                  Last EMR Sync: {formatDisplayDate(lastSyncDate)}
                </p>
              )}
            </div>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              dataSource === 'oracle' ? 'bg-orange-100' : 'bg-blue-100'
            }`}>
              {dataSource === 'oracle' ? (
                <Database className="w-6 h-6 text-orange-600" />
              ) : (
                <Database className="w-6 h-6 text-blue-600" />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Patients</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.total_patients}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Units</p>
                <p className="text-2xl font-bold text-gray-900">{Object.keys(statistics.unit_breakdown).length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Selected</p>
                <p className="text-2xl font-bold text-gray-900">{selectedPatients.length}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Newly Imported</p>
                <p className="text-2xl font-bold text-gray-900">{newlyImportedCount}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Upload className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Month/Year</p>
                <p className="text-lg font-bold text-gray-900">
                  {months[selectedMonth - 1]?.label} {selectedYear}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unit Breakdown */}
      {statistics && Object.keys(statistics.unit_breakdown).length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Unit Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(statistics.unit_breakdown).map(([unitName, count]) => (
              <div key={unitName} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{unitName}</span>
                  <span className="text-2xl font-bold text-blue-600">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Patients Table */}
      {emrData.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">EMR Patients</h3>
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleSelectAll}
                  className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  {selectedPatients.length === emrData.length ? 'Deselect All' : 'Select All'}
                </button>
                {dataSource === 'oracle' ? (
                  <button
                    onClick={() => setShowConfirmationModal(true)}
                    disabled={selectedPatients.length === 0 || importing}
                    className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {importing ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    <span>
                      {importing ? 'Auto-Importing...' : `Auto-Import All (${selectedPatients.length})`}
                    </span>
                  </button>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-600">
                      Data is already imported from local database
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedPatients.length === emrData.length && emrData.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Age
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time of Death
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cause of Death
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Final Diagnosis
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {emrData.map((patient) => (
                  <tr key={patient.PATIENTID} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedPatients.includes(patient.PATIENTID)}
                        onChange={() => handleSelectPatient(patient.PATIENTID)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {patient.PATIENTID}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {patient.AGE}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {patient.unit_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(patient.TIME_OF_DEATH)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {patient.CAUSE_OF_DEATH}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {patient.FINAL_DIAGNOSIS}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data Message */}
      {!loading && emrData.length === 0 && !error && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No EMR Data Found</h3>
          <p className="text-gray-500">
            No patient data found for {months[selectedMonth - 1]?.label} {selectedYear}. 
            Try selecting a different month or year.
          </p>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Auto-Import Confirmation
              </h3>
              <p className="text-gray-600 mb-6">
                You are about to import <strong>{selectedPatients.length}</strong> patients from Oracle EMR.
                Only new patients (not already in the database) will be imported.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowConfirmationModal(false);
                    handleImportSelected();
                  }}
                  className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-200"
                >
                  Confirm Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
